import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  IconButton,
} from "@mui/material";
import { tokens } from "../../theme";
import { Formik } from "formik";
import * as yup from "yup";
import { React, useState, useEffect } from "react";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { iepaProd } from "../../constants/global";
import CloseIcon from "@mui/icons-material/Close";

const CreateLocation = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [locations, setLocations] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate(); // For page navigation

  const handleFormSubmit = (values, { resetForm }) => {
    const data = {
      destinationName: values.location,
      description: "route description",
      code: values.locationCode,
      type: "",
      zipCode: values.pincode,
      activeStartDate: "",
      activeEndDate: "",
      orgId: "1",
      createdBy: 1,
    };
    // Clear previous messages
    setMessage("");
    setError("");
    setIsLoading(true);
    // Save the new location details to the API
    fetch(`${iepaProd.iepaInventorySrvAPI}/inventory/routes/destination/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to save location.");
        }
        return response.json();
      })
      .then((newLocation) => {
        // Update the table with the new data
        setLocations((prevLocations) => [...prevLocations, newLocation]);
        setMessage("Location added successfully!");
        resetForm(); // Reset the form fields after successful submission
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setError("An error occurred while saving the location.");
        setIsLoading(false);
      });
  };

  const handleClose = () => {
    navigate("/Locations"); // Redirect to /CustomerList
  };

  return (
    <Box m="20px">
      <Header
        title="Create Location"
        subtitle="Create a New Location and assign it to Route"
      />
      <IconButton
        onClick={handleClose}
        sx={{ position: "absolute", top: 10, right: 10 }}
      >
        <Typography
          sx={{ fontWeight: "bold", fontSize: "16px", marginRight: "5px" }}
        >
          Close
        </Typography> <CloseIcon />
      </IconButton>
      <Card>
        <CardContent>
          <Formik
            initialValues={initialValues}
            validationSchema={locationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
              resetForm,
            }) => (
              <form onSubmit={handleSubmit}>
                {/* Form fields for Location Code, Pincode, and Location Name */}
                <Box display="flex" justifyContent="space-between">
                  <TextField
                    fullWidth
                    id="locationCode"
                    label="Location Code"
                    value={values.locationCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.locationCode && Boolean(errors.locationCode)}
                    helperText={touched.locationCode && errors.locationCode}
                    margin="normal"
                    sx={{ mr: 2 }}
                  />
                  <TextField
                    fullWidth
                    id="pincode"
                    label="Pincode"
                    value={values.pincode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.pincode && Boolean(errors.pincode)}
                    helperText={touched.pincode && errors.pincode}
                    margin="normal"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <TextField
                    fullWidth
                    id="location"
                    label="Location Name"
                    value={values.location}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location && errors.location}
                    margin="normal"
                    sx={{ mt: 2 }}
                  />

                  <TextField
                    fullWidth
                    id="stage"
                    label="Stage"
                    value={values.stage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.stage && Boolean(errors.stage)}
                    helperText={touched.stage && errors.stage}
                    margin="normal"
                    sx={{ ml: 2 }}
                  />
                </Box>
                {isLoading && <p>Processing...</p>}
                {message && (
                  <Typography color="success.main">{message}</Typography>
                )}
                {error && <Typography color="error.main">{error}</Typography>}
                <Box display="flex" gap={2} sx={{ mt: 3 }}>
                  <Button
                     type="submit"
                     sx={{
                       backgroundColor: colors.blueAccent[700],
                       color: colors.primary[400],
                       fontSize: "14px",
                       fontWeight: "bold",
                       margin: "10px",
                     }}
                     variant="contained"
                  >
                    Save
                  </Button>
                  <Button
                    type="submit"
                    sx={{
                      backgroundColor: colors.blueAccent[700],
                      color: colors.primary[400],
                      fontSize: "14px",
                      fontWeight: "bold",
                      margin: "10px",
                    }}
                    variant="contained"
                    onClick={() => {
                      resetForm(); // Reset form when clicked
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
};

// Validation schema for the form fields
const locationSchema = yup.object().shape({
  locationCode: yup.string().required("Location code is required"),
  pincode: yup.string().required("Pincode is required"),
  location: yup.string().required("Location name is required"),
});

// Initial form values
const initialValues = {
  locationCode: "",
  pincode: "",
  location: "",
  stage: "",
};

export default CreateLocation;
